import React, { useCallback, useEffect, useMemo, useState } from 'react'
import validator from 'validator'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import dayjs from 'dayjs'
import {
  Alert,
  AppBar,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  CardMedia,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'

// Redux Slices
import { getFreeWilayaList } from 'src/redux/slices/tableSlice/wilayaSlice'
import { getFreeCountriesList } from 'src/redux/slices/tableSlice/countrySlice'
import { getFreeNationalitiesList } from 'src/redux/slices/tableSlice/nationalitySlice'
import { getMinInstitutionInfo } from 'src/redux/slices/tableSlice/institutionSlice'
import { registrationStudent } from 'src/redux/slices/teachingSlice/studentSlice'

// Configuration des dates d'inscription (À adapter selon vos besoins)
const START_DATE = dayjs('2026-06-21T00:00:00')
const END_DATE = dayjs('2026-06-30T23:59:59')

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out',
}

const focusedStyle = { borderColor: '#2196f3' }
const acceptStyle = { borderColor: '#00e676' }
const rejectStyle = { borderColor: '#ff1744' }

const Registration = () => {
  const dispatch = useDispatch()
  const lang = 'Français'

  // Formatage des dates pour l'affichage
  const startDateStr = START_DATE.format('DD/MM/YYYY')
  const endDateStr = END_DATE.format('DD/MM/YYYY')

  // Vérification précise de la période
  const now = dayjs()
  const isBeforeStart = now.isBefore(START_DATE)
  const isAfterEnd = now.isAfter(END_DATE)
  const isRegistrationOpen = !isBeforeStart && !isAfterEnd

  // Redux Selectors
  const { minInstitutionData } = useSelector((state) => state.institution)
  const wilaya = useSelector((state) => state.wilaya)
  const country = useSelector((state) => state.country)
  const nationality = useSelector((state) => state.nationality)
  const { isLoading } = useSelector((state) => state.student)

  // Initialisation des données de base
  useEffect(() => {
    dispatch(getMinInstitutionInfo())
    dispatch(getFreeWilayaList())
    dispatch(getFreeCountriesList())
    dispatch(getFreeNationalitiesList())
  }, [dispatch])

  // Optimisation: Utilisation de useMemo pour éviter le recalcul à chaque rendu
  const listWilaya = useMemo(() => {
    return (
      wilaya?.wilayaData?.map((n) => ({
        ID_WIL: n.ID_WIL,
        LIB_WIL: n.LIB_WIL || '',
        LIB_WIL_EN: n.LIB_WIL_EN || '',
      })) || []
    )
  }, [wilaya])

  const listCountry = useMemo(() => {
    return (
      country?.countryData?.map((n) => ({
        ID_PAYS: n.ID_PAYS,
        LIB_PAYS: n.LIB_PAYS || '',
        LIB_PAYS_EN: n.LIB_PAYS_EN || '',
      })) || []
    )
  }, [country])

  const listNationality = useMemo(() => {
    return (
      nationality?.nationalityData?.map((n) => ({
        ID_NAT: n.ID_NAT,
        LIB_NAT: n.LIB_NAT || '',
        LIB_NAT_EN: n.LIB_NAT_EN || '',
      })) || []
    )
  }, [nationality])

  // États locaux
  const [submit, setSubmit] = useState(false)
  const [initWilayaValue, setInitWilayaValue] = useState(null)
  const [initWilayaResidenceValue, setInitWilayaResidenceValue] = useState(null)
  const [initWilayaBacValue, setInitWilayaBacValue] = useState(null)
  const [initCountryValue, setInitCountryValue] = useState(null)
  const [initNationalityValue, setInitNationalityValue] = useState(null)

  const [currentStudent, setCurrentStudent] = useState({
    NOM_ETU: '',
    PRENOM_ETU: '',
    NOM_ETU_AR: '',
    PRENOM_ETU_AR: '',
    DT_NAIS_ETU: null,
    LIEU_NAIS_ETU: '',
    LIEU_NAIS_ETU_AR: '',
    WIL_NAIS_ETU: null,
    PAYS_NAIS_ETU: 1,
    SEXE_ETU: 'Masculin',
    NATIONALITE_ETU: 1,
    ADR_ETU_AR: '',
    ADR_ETU: '',
    WIL_ADR_ETU: null,
    TEL_ETU: '',
    EMAIL_ETU: '',
    MATRICULE_BAC: '',
    ANNEE_BAC: null,
    WIL_BAC_ETU: null,
    FILIERE_BAC: null,
    MOYENNE_BAC: null,
    PRESUME: '0',
    ANNEE_PRESUME: null,
    ETABLISSEMENT: '',
    FILES: null,
  })

  const [formValues, setFormValues] = useState({
    NOM_ETU: {
      error: true,
      errorMessage:
        lang === 'Français' ? `Nom de l'étudiant est requis` : 'Student last name is required',
    },
    PRENOM_ETU: {
      error: true,
      errorMessage:
        lang === 'Français' ? `Prénom de l'étudiant est requis` : 'Student first name is required',
    },
    MATRICULE_BAC: {
      error: true,
      errorMessage:
        lang === 'Français' ? `Matricule du bac est requis` : 'Baccalaureate number is required',
    },
    ANNEE_BAC: {
      error: true,
      errorMessage:
        lang === 'Français'
          ? `L'année du bac est requise`
          : 'The year of the baccalaureate is required',
    },
    EMAIL_ETU: {
      error: true,
      errorMessage: lang === 'Français' ? `Votre email est requis` : 'Your email is required',
    },
    FILES: {
      error: true,
      errorMessage:
        lang === 'Français'
          ? `Vous devez uploader vos documents`
          : 'You must upload your documents',
    },
  })

  // Gestion Dropzone
  const onDrop = useCallback((acceptedFiles) => {}, [])
  const {
    getRootProps,
    getInputProps,
    acceptedFiles,
    fileRejections,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    maxSize: 20480000,
  })

  useEffect(() => {
    if (acceptedFiles?.length !== 0) {
      setFormValues((prev) => ({ ...prev, FILES: { error: false, errorMessage: '' } }))
    } else {
      setFormValues((prev) => ({
        ...prev,
        FILES: {
          error: true,
          errorMessage:
            lang === 'Français'
              ? `Vous devez uploader vos documents`
              : 'You must upload your documents',
        },
      }))
    }
    setCurrentStudent((prev) => ({ ...prev, FILES: acceptedFiles }))
  }, [acceptedFiles])

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject],
  )

  const handleInputChange = (event) => {
    event.preventDefault()
    const { name, value } = event.target

    let error = false
    let errorMessage = ''

    if (name === 'EMAIL_ETU' && !validator.isEmail(value)) {
      error = true
      errorMessage =
        lang === 'Français' ? `Votre email est requis ou invalide` : 'Valid email is required'
    } else if (
      ['NOM_ETU', 'PRENOM_ETU', 'MATRICULE_BAC', 'ANNEE_BAC'].includes(name) &&
      !value.trim()
    ) {
      error = true
      errorMessage = lang === 'Français' ? `Ce champ est requis` : 'This field is required'
    }

    if (formValues[name]) {
      setFormValues((prev) => ({
        ...prev,
        [name]: { error, errorMessage },
      }))
    }

    setCurrentStudent((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const formData = new FormData()
    Object.keys(currentStudent).forEach((key) => {
      if (key === 'FILES') {
        formData.append('FILES', currentStudent[key][0])
      } else {
        formData.append(key, currentStudent[key])
      }
    })
    formData.append('TYPE', 'SC')

    await dispatch(registrationStudent({ formData, lang }))
    setSubmit(true)
  }

  const acceptedFileItems = acceptedFiles.map((file) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
    </li>
  ))

  const fileRejectionItems = fileRejections.map(({ file, errors }) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
      <ul>
        {errors.map((e) => (
          <li key={e.code}>{e.message}</li>
        ))}
      </ul>
    </li>
  ))

  return (
    <>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading || wilaya?.isLoading || country?.isLoading || nationality?.isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ flexGrow: 1 }}>
        <AppBar sx={{ background: '#000000' }} position="static">
          <Toolbar>
            <>
              <CardMedia
                component="img"
                sx={{ width: 200, height: 60 }}
                image={'/logo_universco.png'}
                alt="Logo"
              />
              <Typography
                sx={{ flexGrow: 1, display: { xs: 'none', lg: 'block', xl: 'block' } }}
                variant="h5"
              >
                {minInstitutionData?.LANGUE_DEFAUT_ETAB === 'Français'
                  ? minInstitutionData?.LIB_ETAB?.length > 90
                    ? minInstitutionData?.LIB_ETAB_C
                    : minInstitutionData?.LIB_ETAB
                  : minInstitutionData?.LIB_ETAB_EN?.length > 90
                  ? minInstitutionData?.LIB_ETAB_EN_C
                  : minInstitutionData?.LIB_ETAB_EN}
              </Typography>
              <Button color="inherit">
                <Link
                  to={window.location.origin.replace('admins-', '')}
                  style={{ textDecoration: 'none', color: 'white' }}
                >
                  {lang === 'Français' ? 'Accueil' : 'Home'}
                </Link>
              </Button>
            </>
          </Toolbar>
        </AppBar>
      </Box>

      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justify="center"
        sx={{ margin: 1 }}
      >
        <Grid item>
          <CardMedia
            component="img"
            sx={{
              //width: parseInt(process.env.REACT_APP_LOGO_WIDTH) || 10,
              height: 80,
            }}
            image={`${process.env.REACT_APP_API_URL}/images/logo_institution.png`}
            alt="Logo"
          />
        </Grid>
      </Grid>

      <Box
        component="form"
        sx={{
          maxWidth: '90%',
          margin: 'auto',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
          backgroundColor: 'white',
          marginTop: 2,
        }}
      >
        <Typography variant="h5" component="div" sx={{ mb: 2 }}>
          {lang === 'Français'
            ? `Formulaire d'inscription (Note aux candidats au concours du second cycle)`
            : `Registration form`}
        </Typography>

        {/* Contrôle de l'affichage via la validation des dates */}
        {!isRegistrationOpen ? (
          <Alert variant="filled" severity={isBeforeStart ? 'info' : 'error'} sx={{ mb: 2 }}>
            {isBeforeStart
              ? lang === 'Français'
                ? `Les inscriptions ouvriront du ${startDateStr} au ${endDateStr}.`
                : `Registrations will be open from ${startDateStr} to ${endDateStr}.`
              : lang === 'Français'
              ? `Les inscriptions sont terminées depuis le ${endDateStr}.`
              : `Registrations ended on ${endDateStr}.`}
          </Alert>
        ) : (
          <>
            <Alert variant="outlined" severity="info" sx={{ mb: 2 }}>
              {lang === 'Français'
                ? `Les inscriptions sont actuellement ouvertes. Date limite : ${endDateStr}.`
                : `Registrations are currently open. Deadline: ${endDateStr}.`}
            </Alert>

            <Typography variant="body1" component="div" sx={{ mb: 2 }}>
              Vous êtes tenus à remplir avec soin le formulaire en ligne en veillant à fournir
              toutes les informations demandées de manière claire et complète. Assurez-vous que
              votre dossier de candidature soit complet.
            </Typography>

            {/* Formulaire (Nom/Prénom) */}
            <Grid container spacing={2} mb={2}>
              <Grid item md={4} xs={12}>
                <TextField
                  label={lang === 'Français' ? 'Nom' : 'Last name'}
                  name="NOM_ETU"
                  fullWidth
                  value={currentStudent?.NOM_ETU || ''}
                  onChange={handleInputChange}
                  variant="standard"
                  error={formValues.NOM_ETU.error}
                  helperText={formValues.NOM_ETU.error && formValues.NOM_ETU.errorMessage}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item md={4} xs={12}></Grid>
              <Grid item md={4} xs={12}>
                <TextField
                  sx={{
                    '& label': { left: 'unset', right: '0', transformOrigin: 'right' },
                    '& legend': { textAlign: 'right' },
                    input: { right: 0, textAlign: 'right' },
                  }}
                  fullWidth
                  label="اللقب"
                  onChange={handleInputChange}
                  value={currentStudent?.NOM_ETU_AR || ''}
                  variant="standard"
                  name="NOM_ETU_AR"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} mb={2}>
              <Grid item md={4} xs={12}>
                <TextField
                  label={lang === 'Français' ? 'Prénom' : 'First name'}
                  name="PRENOM_ETU"
                  fullWidth
                  value={currentStudent?.PRENOM_ETU || ''}
                  onChange={handleInputChange}
                  variant="standard"
                  error={formValues.PRENOM_ETU.error}
                  helperText={formValues.PRENOM_ETU.error && formValues.PRENOM_ETU.errorMessage}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item md={4} xs={12}></Grid>
              <Grid item md={4} xs={12}>
                <TextField
                  sx={{
                    '& label': { left: 'unset', right: '0', transformOrigin: 'right' },
                    '& legend': { textAlign: 'right' },
                    input: { right: 0, textAlign: 'right' },
                  }}
                  fullWidth
                  label="الإسم"
                  onChange={handleInputChange}
                  value={currentStudent?.PRENOM_ETU_AR || ''}
                  variant="standard"
                  name="PRENOM_ETU_AR"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            {/* Sexe */}
            <Grid container spacing={2} mb={2}>
              <Grid item md={4} xs={12}>
                <FormControl sx={{ width: 1 }} variant="standard">
                  <InputLabel>{lang === 'Français' ? 'Sexe' : 'Sex'}</InputLabel>
                  <Select
                    fullWidth
                    value={currentStudent?.SEXE_ETU || 'Masculin'}
                    label="Sexe"
                    onChange={handleInputChange}
                    variant="standard"
                    name="SEXE_ETU"
                  >
                    <MenuItem value="Masculin">
                      {lang === 'Français' ? 'Masculin' : 'Male'}
                    </MenuItem>
                    <MenuItem value="Féminin">
                      {lang === 'Français' ? 'Féminin' : 'Female'}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Date et lieu de naissance */}
            <Grid container spacing={2} mb={2}>
              <Grid item md={4} xs={12}>
                <DatePicker
                  inputFormat="DD/MM/YYYY"
                  label={lang === 'Français' ? 'Date de naissance' : 'Date of birth'}
                  value={dayjs(currentStudent?.DT_NAIS_ETU) || ''}
                  slotProps={{ textField: { variant: 'standard', fullWidth: true } }}
                  onChange={(date) =>
                    setCurrentStudent({ ...currentStudent, DT_NAIS_ETU: new Date(date) })
                  }
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <TextField
                  label={lang === 'Français' ? 'Lieu de naissance' : 'Place of birth'}
                  name="LIEU_NAIS_ETU"
                  fullWidth
                  value={currentStudent?.LIEU_NAIS_ETU || ''}
                  onChange={handleInputChange}
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <TextField
                  sx={{
                    '& label': { left: 'unset', right: '0', transformOrigin: 'right' },
                    input: { right: 0, textAlign: 'right' },
                  }}
                  fullWidth
                  label="مكان الإزدياد"
                  onChange={handleInputChange}
                  value={currentStudent?.LIEU_NAIS_ETU_AR || ''}
                  variant="standard"
                  name="LIEU_NAIS_ETU_AR"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            {/* Wilaya de naissance */}
            <Grid container spacing={2} mb={2}>
              <Grid item md={4} xs={12}>
                <Autocomplete
                  options={listWilaya}
                  getOptionLabel={(option) =>
                    lang === 'Français' ? option?.LIB_WIL || '' : option?.LIB_WIL_EN || ''
                  }
                  value={initWilayaValue}
                  onChange={(e, value) => {
                    setInitWilayaValue(value || null)
                    setCurrentStudent({ ...currentStudent, WIL_NAIS_ETU: value?.ID_WIL || '' })
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label={lang === 'Français' ? 'Wilaya de naissance' : 'Wilaya of birth'}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Pays et Nationalité */}
            <Grid container spacing={2} mb={2}>
              <Grid item md={4} xs={12}>
                <Autocomplete
                  options={listCountry}
                  getOptionLabel={(option) =>
                    lang === 'Français' ? option?.LIB_PAYS || '' : option?.LIB_PAYS_EN || ''
                  }
                  value={initCountryValue}
                  onChange={(e, value) => {
                    setInitCountryValue(value || null)
                    setCurrentStudent({ ...currentStudent, PAYS_NAIS_ETU: value?.ID_PAYS || '' })
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label={lang === 'Français' ? 'Pays de naissance' : 'Country of birth'}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} mb={2}>
              <Grid item md={4} xs={12}>
                <Autocomplete
                  options={listNationality}
                  getOptionLabel={(option) =>
                    lang === 'Français' ? option?.LIB_NAT || '' : option?.LIB_NAT_EN || ''
                  }
                  value={initNationalityValue}
                  onChange={(e, value) => {
                    setInitNationalityValue(value || null)
                    setCurrentStudent({ ...currentStudent, NATIONALITE_ETU: value?.ID_NAT || '' })
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label={lang === 'Français' ? 'Nationalité' : 'Nationality'}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Adresse */}
            <Grid container spacing={2} mb={2}>
              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label={lang === 'Français' ? 'Adresse' : 'Address'}
                  onChange={handleInputChange}
                  value={currentStudent?.ADR_ETU || ''}
                  variant="standard"
                  name="ADR_ETU"
                  multiline
                  rows={2}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <TextField
                  sx={{ '& label': { left: 'unset', right: '0', transformOrigin: 'right' } }}
                  inputProps={{ style: { textAlign: 'right' } }}
                  fullWidth
                  label="العنوان"
                  onChange={handleInputChange}
                  value={currentStudent?.ADR_ETU_AR || ''}
                  variant="standard"
                  name="ADR_ETU_AR"
                  multiline
                  rows={2}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            {/* Wilaya de résidence */}
            <Grid container spacing={2} mb={2}>
              <Grid item md={4} xs={12}>
                <Autocomplete
                  options={listWilaya}
                  getOptionLabel={(option) =>
                    lang === 'Français' ? option?.LIB_WIL || '' : option?.LIB_WIL_EN || ''
                  }
                  value={initWilayaResidenceValue}
                  onChange={(e, value) => {
                    setInitWilayaResidenceValue(value || null)
                    setCurrentStudent({ ...currentStudent, WIL_ADR_ETU: value?.ID_WIL || '' })
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label={lang === 'Français' ? 'Wilaya de résidence' : 'Wilaya of residence'}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Téléphone et Mail */}
            <Grid container spacing={2} mb={2}>
              <Grid item md={3} xs={12}>
                <TextField
                  label="Téléphone"
                  name="TEL_ETU"
                  fullWidth
                  value={currentStudent?.TEL_ETU || ''}
                  onChange={handleInputChange}
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item md={3} xs={12}>
                <TextField
                  label="Mail personnel"
                  name="EMAIL_ETU"
                  fullWidth
                  value={currentStudent?.EMAIL_ETU || ''}
                  onChange={handleInputChange}
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                  error={formValues.EMAIL_ETU.error}
                  helperText={formValues.EMAIL_ETU.error && formValues.EMAIL_ETU.errorMessage}
                />
              </Grid>
            </Grid>

            {/* Informations BAC */}
            <Grid container spacing={2} mb={2}>
              <Grid item md={2} xs={12}>
                <TextField
                  label="Matricule du BAC"
                  name="MATRICULE_BAC"
                  fullWidth
                  value={currentStudent?.MATRICULE_BAC || ''}
                  onChange={handleInputChange}
                  variant="standard"
                  error={formValues.MATRICULE_BAC.error}
                  helperText={
                    formValues.MATRICULE_BAC.error && formValues.MATRICULE_BAC.errorMessage
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item md={2} xs={12}>
                <TextField
                  label="Moyenne du BAC"
                  name="MOYENNE_BAC"
                  type="number"
                  fullWidth
                  value={currentStudent?.MOYENNE_BAC || ''}
                  onChange={handleInputChange}
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item md={2} xs={12}>
                <TextField
                  label="Année du BAC"
                  name="ANNEE_BAC"
                  fullWidth
                  type="number"
                  value={currentStudent?.ANNEE_BAC || ''}
                  onChange={handleInputChange}
                  variant="standard"
                  error={formValues.ANNEE_BAC.error}
                  helperText={formValues.ANNEE_BAC.error && formValues.ANNEE_BAC.errorMessage}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item md={3} xs={12}>
                <Autocomplete
                  options={listWilaya}
                  getOptionLabel={(option) =>
                    lang === 'Français' ? option?.LIB_WIL || '' : option?.LIB_WIL_EN || ''
                  }
                  value={initWilayaBacValue}
                  onChange={(e, value) => {
                    setInitWilayaBacValue(value || null)
                    setCurrentStudent({ ...currentStudent, WIL_BAC_ETU: value?.ID_WIL || '' })
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label="Wilaya du BAC"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Établissement d'origine */}
            <Grid container spacing={2} mb={2}>
              <Grid item md={6} xs={12}>
                <TextField
                  label="Etablissement d'origine"
                  name="ETABLISSEMENT"
                  fullWidth
                  value={currentStudent?.ETABLISSEMENT || ''}
                  onChange={handleInputChange}
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            {/* Upload PDF */}
            <Grid container spacing={2} mb={2}>
              <Grid item md={12} xs={12}>
                <Typography variant="h6" gutterBottom>
                  Relevé de notes du BAC, 1re année, 2e année et certificat d'inscription (un seul
                  fichier PDF)
                </Typography>
                <div className="container">
                  <div {...getRootProps({ style })}>
                    <input {...getInputProps()} />
                    <Typography>
                      Glissez/déplacez ou sélectionnez votre fichier au format PDF
                    </Typography>
                  </div>
                </div>
                <aside>
                  {formValues?.FILES.error && (
                    <Typography
                      variant="caption"
                      display="block"
                      gutterBottom
                      sx={{ color: 'red' }}
                    >
                      {formValues?.FILES.errorMessage}
                    </Typography>
                  )}
                  <Typography variant="subtitle2" gutterBottom>
                    Fichiers acceptés
                  </Typography>
                  <ul>{acceptedFileItems}</ul>
                  <Typography variant="subtitle2" gutterBottom>
                    Fichiers rejetés
                  </Typography>
                  <ul>{fileRejectionItems}</ul>
                </aside>
              </Grid>
            </Grid>

            {/* Bouton de soumission */}
            {submit && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Votre inscription a été effectuée avec succès. Veuillez consulter votre email.
              </Alert>
            )}

            <Grid container spacing={2} mb={2}>
              <Grid item md={12} xs={12}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSubmit}
                  disabled={
                    formValues?.NOM_ETU.error ||
                    formValues?.PRENOM_ETU.error ||
                    formValues?.MATRICULE_BAC.error ||
                    formValues?.EMAIL_ETU.error ||
                    formValues?.FILES.error
                  }
                >
                  Envoyer
                </Button>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </>
  )
}

export default Registration
